import { useInfiniteQuery, useQuery } from '@tanstack/react-query'
import { uniqBy } from 'lodash-es'
import { useCallback, useMemo } from 'react'
import type { ArtworkFeedResult } from 'src/components/ArtworkList/useArtworkFeed'
import { orpcClient } from './client'
import {
  tackCuratedArtworksInfiniteOptions,
  tackCuratedArtworksOptions,
} from './generated/@tanstack/react-query.gen'
import type { TackCuratedArtworksResponse } from './generated/types.gen'
import { toArtworkPreviewFragment } from './utils/typeAdapters'

export type TackCuratedArtworkItem = TackCuratedArtworksResponse['data'][number]

export function useTackCuratedArtworks(
  slug: string,
  page?: number,
  pageSize = 24,
) {
  return useQuery({
    ...tackCuratedArtworksOptions({
      client: orpcClient,
      path: { slug },
      query: { page, pageSize },
    }),
    staleTime: 30_000,
    throwOnError: true,
  })
}

export function useTackCuratedArtworksInfinite({
  slug,
  pageSize = 24,
}: {
  slug: string | null
  pageSize?: number
}): ArtworkFeedResult {
  const result = useInfiniteQuery({
    ...tackCuratedArtworksInfiniteOptions({
      client: orpcClient,
      path: { slug: slug ?? '' },
      query: { pageSize },
    }),
    enabled: slug != null,
    initialPageParam: 1,
    getNextPageParam: lastPage =>
      lastPage.hasNextPage ? (lastPage.page ?? 1) + 1 : undefined,
    staleTime: 30_000,
    // liked depends on the current user — reset on login/logout.
    meta: { dependsOn: ['login'] },
  })

  const artworks = useMemo(
    () =>
      uniqBy(
        result.data?.pages.flatMap(page =>
          page.data.map(item => toArtworkPreviewFragment(item)),
        ) ?? [],
        'id',
      ),
    [result.data?.pages],
  )

  const loadMore = useCallback(() => {
    if (result.isLoading || result.isFetchingNextPage || !result.hasNextPage)
      return
    result.fetchNextPage()
  }, [result])

  return {
    artworks,
    loading: result.isLoading,
    loadingMore: result.isFetchingNextPage,
    hasNext: result.hasNextPage ?? false,
    loadMore,
  }
}
